import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, ExternalLink, Newspaper, X, ZoomIn } from 'lucide-react';

// ---- Lightbox ----
function Lightbox({ items, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const item = items[idx];

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div onClick={onBackdrop} style={{
      position: 'fixed', inset: 0, background: 'rgba(5,8,15,0.97)',
      backdropFilter: 'blur(16px)', zIndex: 9000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <button onClick={onClose} style={{
        position: 'absolute', top: '20px', right: '20px',
        background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '50%', width: '48px', height: '48px',
        color: '#F5E6D3', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
      }}><X size={22} /></button>

      {items.length > 1 && idx > 0 && (
        <button onClick={() => setIdx(i => i - 1)} style={{
          position: 'absolute', left: '16px', background: 'rgba(5,8,15,0.75)',
          border: '1px solid rgba(212,175,55,0.35)', borderRadius: '50%', width: '48px', height: '48px',
          color: '#D4AF37', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
        }}><ChevronLeft size={24} /></button>
      )}

      <div style={{ maxWidth: '92vw', maxHeight: '88vh' }}>
        {item.type === 'image' ? (
          <img src={item.src} alt="" style={{
            maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain',
            borderRadius: '12px', boxShadow: '0 24px 80px rgba(0,0,0,0.9)', display: 'block',
          }} />
        ) : (
          <video src={item.src} autoPlay loop playsInline controls
            style={{ maxWidth: '92vw', maxHeight: '85vh', borderRadius: '12px', boxShadow: '0 24px 80px rgba(0,0,0,0.9)', display: 'block' }} />
        )}
      </div>

      {items.length > 1 && idx < items.length - 1 && (
        <button onClick={() => setIdx(i => i + 1)} style={{
          position: 'absolute', right: '16px', background: 'rgba(5,8,15,0.75)',
          border: '1px solid rgba(212,175,55,0.35)', borderRadius: '50%', width: '48px', height: '48px',
          color: '#D4AF37', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2,
        }}><ChevronRight size={24} /></button>
      )}

      {items.length > 1 && (
        <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '8px' }}>
          {items.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} style={{
              width: i === idx ? '22px' : '8px', height: '8px', borderRadius: '4px',
              background: i === idx ? '#D4AF37' : 'rgba(212,175,55,0.3)',
              border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.25s',
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

// Static media data (URLs don't need translation)
const NEWS_MEDIA = {
  3: {
    videos: [],
    images: [
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/bkznmgv4_news%203%20pic1.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/ympi3vz3_news%203%20pic2.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/ewue9dx6_newx%203%20pic3.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/988z225a_news%203%20pic4.jif',
    ],
    link: 'https://x.com/AllInX_Exchange/status/2048778883709997249?s=20',
    hashtags: ['#AllinX', '#NobodyGlobal', '#RoyalFamily', '#PattayaGala', '#Web3Elite', '#StrategicPrestige', '#GlobalConsensus'],
  },
  1: {
    videos: [
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/646xbhqh_news%201%20vid%201.mp4',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/82pail78_news%201%20vid%202.mp4',
    ],
    images: [
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/c6bdsq28_news%201%20pic1.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/78g7a7kl_news%201%20pic2.jif',
    ],
    link: 'https://x.com/AllInX_Exchange/status/2049096195273109837?s=20',
    hashtags: ['#Contributor', '#CommunitySpirit', '#BuilderResonance', '#GratefulToHaveYou', '#AllinX'],
  },
  2: {
    videos: [],
    images: [
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/ta2kq7ht_news%202%20pic1.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/h35wsqhk_news%202%20pic2.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/rsm1nl4b_news%202%20pic3.jif',
      'https://customer-assets.emergentagent.com/job_spa-magic-portal/artifacts/3dt9bwzy_news%202%20pic4.jif',
    ],
    link: 'https://x.com/AllInX_Exchange/status/2049085734079098887?s=20',
    hashtags: ['#Blockchain', '#AI', '#Web3', '#Decentralization', '#AllinX'],
  },
};

const NEWS_ORDER = [1, 3, 2]; // AllinX Thailand first

function VideoPlayer({ src }) {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', background: '#020408' }}>
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        style={{ width: '100%', display: 'block', maxHeight: '340px', objectFit: 'cover' }}
      />
      <button
        onClick={toggleMute}
        style={{
          position: 'absolute', bottom: '10px', right: '10px',
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '6px 12px',
          background: 'rgba(5,8,15,0.75)',
          border: '1px solid rgba(212,175,55,0.4)',
          borderRadius: '20px',
          color: '#D4AF37',
          fontFamily: 'Montserrat,sans-serif',
          fontSize: '0.72rem', fontWeight: 600,
          cursor: 'pointer',
          backdropFilter: 'blur(8px)',
        }}
        aria-label={muted ? 'Sound On' : 'Sound Off'}
      >
        {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
        {muted ? 'Sound On' : 'Sound Off'}
      </button>
    </div>
  );
}

export const NewsSection = () => {
  const { t } = useTranslation();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null); // { items, startIndex }

  const newsId = NEWS_ORDER[currentIdx];
  const media = NEWS_MEDIA[newsId];
  const total = NEWS_ORDER.length;
  const item = t(`news_section.items.${newsId}`, { returnObjects: true });

  return (
    <section
      id="news"
      style={{ padding: '100px 0', background: 'var(--color-surface)', position: 'relative' }}
      data-testid="news-section"
    >
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)' }} />

      <div className="container">
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '48px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <p className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Newspaper size={14} />
              News
            </p>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem, 3vw, 3rem)', margin: 0 }}>
              {t('news_section.section_title')}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => setCurrentIdx(i => Math.max(0, i - 1))}
              disabled={currentIdx === 0}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px',
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                color: currentIdx === 0 ? 'var(--color-text-dim)' : 'var(--color-gold)',
                cursor: currentIdx === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: '0.8rem', fontWeight: 500,
                opacity: currentIdx === 0 ? 0.4 : 1,
              }}
            >
              <ChevronLeft size={15} /> {t('news_section.newer')}
            </button>
            <span style={{ color: 'var(--color-text-dim)', fontFamily: 'var(--font-sans)', fontSize: '0.78rem' }}>
              {currentIdx + 1} / {total}
            </span>
            <button
              onClick={() => setCurrentIdx(i => Math.min(total - 1, i + 1))}
              disabled={currentIdx === total - 1}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px',
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                color: currentIdx === total - 1 ? 'var(--color-text-dim)' : 'var(--color-gold)',
                cursor: currentIdx === total - 1 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: '0.8rem', fontWeight: 500,
                opacity: currentIdx === total - 1 ? 0.4 : 1,
              }}
            >
              {t('news_section.older')} <ChevronRight size={15} />
            </button>
          </div>
        </div>

        {/* News card */}
        <div style={{
          background: 'var(--color-surface-2)',
          border: '1px solid var(--color-border)',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-gold)',
        }}>
          <div style={{ padding: '32px 36px' }}>
            {/* Title */}
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.4rem, 2.5vw, 2.1rem)',
              color: 'var(--color-text)',
              lineHeight: 1.25,
              marginBottom: '28px',
              fontWeight: 600,
            }}>
              {item.title}
            </h3>

            {/* Media grid */}
            {(media.videos.length > 0 || media.images.length > 0) && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: media.images.length >= 3 || media.videos.length >= 2 ? 'repeat(2, 1fr)' : media.videos.length + media.images.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                gap: '12px',
                marginBottom: '28px',
              }}>
                {(() => {
                  const allMedia = [
                    ...media.videos.map(src => ({ type: 'video', src })),
                    ...media.images.map(src => ({ type: 'image', src })),
                  ];
                  return (
                    <>
                      {media.videos.map((src, i) => (
                        <div key={`v${i}`} style={{ position: 'relative', cursor: 'pointer' }}
                          onClick={() => setLightbox({ items: allMedia, startIndex: i })}>
                          <VideoPlayer src={src} />
                          <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(5,8,15,0.65)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px', padding: '4px 8px', color: '#D4AF37', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', pointerEvents: 'none' }}>
                            <ZoomIn size={11} /> Maximieren
                          </div>
                        </div>
                      ))}
                      {media.images.map((src, i) => (
                        <div key={`img${i}`}
                          style={{ borderRadius: '12px', overflow: 'hidden', background: '#020408', cursor: 'pointer', position: 'relative', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                          onClick={() => setLightbox({ items: allMedia, startIndex: media.videos.length + i })}
                          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; e.currentTarget.style.boxShadow = '0 8px 32px rgba(212,175,55,0.2)'; }}
                          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                          <img src={src} alt={`${item.title} ${i + 1}`} loading="lazy"
                            style={{ width: '100%', display: 'block', maxHeight: '340px', objectFit: 'cover' }} />
                          <div style={{ position: 'absolute', bottom: '10px', right: '10px', background: 'rgba(5,8,15,0.65)', border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px', padding: '4px 8px', color: '#D4AF37', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <ZoomIn size={11} /> Maximieren
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            )}

            {/* Body paragraphs */}
            <div style={{ marginBottom: '24px' }}>
              {['body1', 'body2', 'body3'].map((key, i) => item[key] && (
                <p key={key} style={{
                  color: i === 0 ? 'var(--color-text)' : 'var(--color-text-muted)',
                  fontFamily: i === 0 ? 'var(--font-serif)' : 'var(--font-sans)',
                  fontSize: i === 0 ? '1.05rem' : '0.92rem',
                  fontStyle: i === 0 ? 'italic' : 'normal',
                  lineHeight: 1.8,
                  marginBottom: '14px',
                }}>
                  {item[key]}
                </p>
              ))}
            </div>

            {/* Hashtags */}
            {media.hashtags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
                {media.hashtags.map((tag, i) => (
                  <span key={i} style={{
                    padding: '4px 12px',
                    background: 'rgba(0,191,255,0.07)',
                    border: '1px solid rgba(0,191,255,0.2)',
                    borderRadius: '20px',
                    color: 'rgba(0,191,255,0.85)',
                    fontFamily: 'var(--font-sans)', fontSize: '0.75rem', fontWeight: 500,
                  }}>{tag}</span>
                ))}
              </div>
            )}

            {/* Source link */}
            {media.link && (
              <a href={media.link} target="_blank" rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '7px',
                  padding: '10px 20px',
                  background: 'rgba(212,175,55,0.08)',
                  border: '1px solid rgba(212,175,55,0.3)',
                  borderRadius: '8px',
                  color: 'var(--color-gold)',
                  fontFamily: 'var(--font-sans)', fontSize: '0.82rem', fontWeight: 600,
                  textDecoration: 'none',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(212,175,55,0.08)'; }}
              >
                <ExternalLink size={14} /> {t('news_section.source')}
              </a>
            )}
          </div>
        </div>

        {/* Dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
          {NEWS_ORDER.map((_, i) => (
            <button key={i} onClick={() => setCurrentIdx(i)} style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: i === currentIdx ? 'var(--color-gold)' : 'rgba(212,175,55,0.25)',
              border: 'none', cursor: 'pointer', padding: 0,
              transform: i === currentIdx ? 'scale(1.4)' : 'scale(1)',
              transition: 'all 0.2s ease',
            }} aria-label={`News ${i + 1}`} />
          ))}
        </div>
      </div>

      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)' }} />
      {lightbox && <Lightbox items={lightbox.items} startIndex={lightbox.startIndex} onClose={() => setLightbox(null)} />}
    </section>
  );
};
